import { useEffect, useState, type ReactNode } from 'react';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';

export interface ServerSearchComboboxProps<T> {
  /** Currently selected item id (empty/undefined when nothing is selected). */
  value?: string;
  /**
   * Called with the newly selected item's id and the item itself. Re-selecting
   * the current value clears it (id `undefined`), matching the existing selector
   * toggle behaviour.
   */
  onChange: (value: string | undefined, item: T | undefined) => void;
  /**
   * Items to render for the current (debounced) search term. The caller owns
   * fetching/filtering — this primitive never filters client-side, so pass the
   * already-resolved result set.
   */
  items: T[];
  /** Stable id extractor for an item. */
  getItemId: (item: T) => string;
  /** Renders a single row inside the list. `isSelected` reflects `value`. */
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  /**
   * Renders the trigger's inner content. Receives the resolved selected item
   * (or `undefined` when nothing is selected — render the placeholder then).
   */
  renderTrigger: (selectedItem: T | undefined) => ReactNode;
  /**
   * The selected item, resolved by the caller. Kept separate from `items`
   * because under server-side search the selected row is frequently absent from
   * the current result page — the caller resolves it by id so the trigger label
   * never goes blank.
   */
  selectedItem?: T;
  /**
   * Fires with the debounced search term whenever it settles. Server-side
   * callers forward it to their query hook; small in-memory callers filter on
   * it. Also fires with `''` when the popover closes and the term resets.
   */
  onDebouncedSearchChange?: (search: string) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  /**
   * The caller's query failed. Kept distinct from an empty `items` array: both
   * leave the list with nothing to render, but "no matches" and "the search
   * never completed" are opposite facts and must not share a message.
   */
  isError?: boolean;
  disabled?: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
  loadingMessage: string;
  /**
   * Shown instead of `emptyMessage` when `isError` is set and nothing is in
   * flight. Optional: callers that pass no message keep the previous behaviour.
   */
  errorMessage?: string;
  /**
   * Optional footer rendered below the list, e.g. a "Create new…" action. Passed
   * a `close` callback that shuts the popover and clears the search term.
   */
  renderFooter?: (close: () => void) => ReactNode;
  debounceMs?: number;
  align?: 'start' | 'center' | 'end';
  contentClassName?: string;
  triggerClassName?: string;
  triggerAriaLabel?: string;
  /**
   * Put on the trigger button so a `<label htmlFor>` resolves to the control.
   * Without it the field's label and the control are not associated.
   */
  triggerId?: string;
  /** Forwarded to the trigger as `aria-describedby` (error / description text). */
  triggerAriaDescribedBy?: string;
  /** Forwarded to the trigger as `aria-invalid` (the field has an error). */
  triggerAriaInvalid?: boolean | 'true' | 'false';
  /** Forwarded to the trigger as `aria-required` (`FormLabel required`). */
  triggerAriaRequired?: boolean | 'true' | 'false';
  /**
   * Button variant for the trigger. Defaults to `outline` (the field-style
   * trigger). Toolbar callers pass `ghost` so the control reads as an icon
   * button rather than a bordered form field.
   */
  triggerVariant?: ButtonProps['variant'];
  /**
   * Button size for the trigger. Left undefined by default so the existing
   * default size is preserved; icon-only triggers pass `icon`.
   */
  triggerSize?: ButtonProps['size'];
  /**
   * Renders the trailing `ChevronsUpDown` affordance. Defaults to `true`.
   * Icon-only triggers turn it off — a chevron next to a 36 px icon button
   * leaves no room for the icon itself.
   */
  showTriggerChevron?: boolean;
}

/**
 * Reusable server-search combobox: a debounced, type-to-search single-select
 * built on Command + Popover. Data fetching stays with the caller (its query
 * hook), keeping RLS/pagination concerns in one place; this primitive owns the
 * open/search UI state, debounce, reset-on-close, loading/empty rendering, and
 * the selected-label-outside-results problem.
 */
export function ServerSearchCombobox<T>({
  value,
  onChange,
  items,
  getItemId,
  renderItem,
  renderTrigger,
  selectedItem,
  onDebouncedSearchChange,
  isLoading = false,
  isFetching = false,
  isError = false,
  disabled = false,
  searchPlaceholder,
  emptyMessage,
  loadingMessage,
  errorMessage,
  renderFooter,
  debounceMs = 300,
  align = 'start',
  contentClassName,
  triggerClassName,
  triggerAriaLabel,
  triggerId,
  triggerAriaDescribedBy,
  triggerAriaInvalid,
  triggerAriaRequired,
  triggerVariant = 'outline',
  triggerSize,
  showTriggerChevron = true,
}: ServerSearchComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedSearch(search, debounceMs);

  // Surface the debounced term to the caller so it can fetch/filter.
  useEffect(() => {
    onDebouncedSearchChange?.(debouncedSearch);
  }, [debouncedSearch, onDebouncedSearchChange]);

  // Reset the term whenever the popover closes so the next open starts clean.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const close = () => {
    setSearch('');
    setOpen(false);
  };

  const showSpinner = isFetching && !isLoading;
  // A failed load is not an empty result set. Without this the list answers a
  // request that never completed with "no matches found" — the caller's error is
  // reported as a confident, wrong fact about the data.
  const showError = isError && !isLoading && !isFetching && !!errorMessage;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={triggerId}
          variant={triggerVariant}
          size={triggerSize}
          role="combobox"
          aria-expanded={open}
          aria-label={triggerAriaLabel}
          aria-describedby={triggerAriaDescribedBy}
          aria-invalid={triggerAriaInvalid}
          aria-required={triggerAriaRequired}
          disabled={disabled}
          className={cn('w-full justify-between', triggerClassName)}
        >
          {renderTrigger(selectedItem)}
          {showTriggerChevron && (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', contentClassName)}
        align={align}
      >
        <Command shouldFilter={false}>
          <div className="relative">
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            {showSpinner && (
              <Loader2
                className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            )}
          </div>
          <CommandList>
            <CommandEmpty>
              {isLoading || isFetching ? (
                loadingMessage
              ) : showError ? (
                <span role="alert" data-testid="server-search-error">
                  {errorMessage}
                </span>
              ) : (
                emptyMessage
              )}
            </CommandEmpty>
            {items.length > 0 && (
              <CommandGroup>
                {items.map((item) => {
                  const id = getItemId(item);
                  const isSelected = value === id;
                  return (
                    <CommandItem
                      key={id}
                      value={id}
                      onSelect={() => {
                        onChange(isSelected ? undefined : id, isSelected ? undefined : item);
                        close();
                      }}
                    >
                      {renderItem(item, isSelected)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {renderFooter && <CommandGroup>{renderFooter(close)}</CommandGroup>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ServerSearchCombobox;
